import { Controller, FieldError, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Grid, Switch, FormControlLabel, Box, IconButton, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { Delete, Refresh } from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

import { ICouponCreateFormType } from "./form-type";
import { createCouponValidationSchema } from "./validation";

import FormText from "@/components/kit/FormGenerator/FormText";
import FormSelect from "@/components/kit/FormGenerator/FormSelect";
import FormTextarea from "@/components/kit/FormGenerator/FormTextarea";
import FormMultiSelect from "@/components/kit/FormGenerator/FormMultiSelect";
import { useGetCouponById, useGenerateCouponCode } from "@/api/hooks/useCoupons";
import { usePlansList } from "@/api/hooks/usePlans";

// Get user's timezone
const getUserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return "UTC";
    }
};

const userTimezone = getUserTimezone();

const defaultValues: ICouponCreateFormType = {
    title: "",
    description: undefined,
    codes: [""],
    amountType: "FIXED",
    amountValue: 0,
    startDate: dayjs().tz(userTimezone).format("YYYY-MM-DDTHH:mm"),
    expireDate: undefined,
    appliedTo: "ALL",
    usagePerUser: undefined,
    usageLimit: undefined,
    minimumPrice: undefined,
    plans: [],
    active: true,
    isCustomCode: true,
    showStartDate: false,
    showUsagePerUser: false,
    showUsageLimit: false,
    showMinimumPrice: false,
};

export default function CouponForm({
    onSubmit,
    loading,
}: {
    loading: boolean,
    onSubmit: (data: ICouponCreateFormType, autoCodeCount?: number) => void,
}) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [codes, setCodes] = useState<string[]>([""]);
    const [hasExpireDate, setHasExpireDate] = useState<boolean>(false);
    const [isCustomCode, setIsCustomCode] = useState<boolean>(true);
    const [autoCodeCount, setAutoCodeCount] = useState<number>(1);
    const [showStartDate, setShowStartDate] = useState<boolean>(false);
    const [showUsagePerUser, setShowUsagePerUser] = useState<boolean>(false);
    const [showUsageLimit, setShowUsageLimit] = useState<boolean>(false);
    const [showMinimumPrice, setShowMinimumPrice] = useState<boolean>(false);

    const { data: couponData } = useGetCouponById(id ? Number(id) : 0);
    const { data: plansData } = usePlansList({});
    const { mutate: generateCode, isPending: isGenerating } = useGenerateCouponCode();

    const {
        control,
        formState: { errors, isValid },
        handleSubmit,
        reset,
        trigger,
        watch,
        setValue,
        clearErrors,
    } = useForm<ICouponCreateFormType>({
        defaultValues,
        mode: "all",
        resolver: yupResolver(createCouponValidationSchema),
    });

    const appliedTo = watch("appliedTo");
    const amountType = watch("amountType");

    useEffect(() => {
        if (isValid) void trigger();
    }, [trigger, isValid]);

    useEffect(() => {
        if (couponData?.data) {
            const coupon = couponData.data;
            const hasExpire = !!coupon.expireDate;
            const hasStart = !!coupon.startDate;
            setHasExpireDate(hasExpire);
            setShowStartDate(hasStart);

            reset({
                title: coupon.title || "",
                description: coupon.description,
                codes: coupon.codes?.map((code) => code.code) || [""],
                amountType: coupon.amountType,
                amountValue: coupon.amountValue ? Number(coupon.amountValue) : 0,
                startDate: hasStart ? dayjs(coupon.startDate).tz(userTimezone).format("YYYY-MM-DDTHH:mm") : undefined,
                expireDate: hasExpire ? dayjs(coupon.expireDate).tz(userTimezone).format("YYYY-MM-DDTHH:mm") : undefined,
                appliedTo: coupon.appliedTo,
                usagePerUser: coupon.usagePerUser,
                usageLimit: coupon.usageLimit,
                minimumPrice: coupon.minimumPrice ? Number(coupon.minimumPrice) : undefined,
                plans: coupon.plans?.map((plan: any) => (typeof plan === "object" ? plan.id : plan)) || [],
                active: coupon.status === "ACTIVE",
                showStartDate: hasStart,
                showUsagePerUser: !!coupon.usagePerUser,
                showUsageLimit: !!coupon.usageLimit,
                showMinimumPrice: !!coupon.minimumPrice,
            });
            setCodes(coupon.codes?.map((code) => code.code) || [""]);
            setShowUsagePerUser(!!coupon.usagePerUser);
            setShowUsageLimit(!!coupon.usageLimit);
            setShowMinimumPrice(!!coupon.minimumPrice);
        }
    }, [couponData, reset]);

    const handleAddCode = () => {
        const newCodes = [...codes, ""];
        setCodes(newCodes);
        setValue("codes", newCodes);
    };

    const handleRemoveCode = (index: number) => {
        if (codes.length > 1) {
            const newCodes = codes.filter((_, i) => i !== index);
            setCodes(newCodes);
            setValue("codes", newCodes);
        }
    };

    const handleCodeChange = (index: number, value: string) => {
        const newCodes = [...codes];
        newCodes[index] = value;
        setCodes(newCodes);
        setValue("codes", newCodes);
    };

    const amountTypeOptions = [
        { label: "Fixed Amount", value: "FIXED" },
        { label: "Percentage", value: "PERCENTAGE" },
    ];

    const appliedToOptions = [
        { label: "All", value: "ALL" },
        { label: "First Subscription", value: "FIRST_SUBSCRIPTION" },
    ];

    const planOptions =
        plansData?.data?.items
            ?.filter((plan) => plan.isActive) // Only show active plans
            ?.map((plan) => ({
                label: plan.ml_title.en || plan.ml_title.ar || `Plan ${plan.id}`,
                value: plan.id,
            })) || [];

    const handleFormSubmit = (data: ICouponCreateFormType) => {
        console.log("🚀 handleFormSubmit called with data:", data);

        // If auto mode, remove codes field completely to avoid validation issues
        let formData: any = { ...data, isCustomCode };

        // Remove codes field if auto mode
        if (!isCustomCode) {
            delete formData.codes;
            setValue("codes", undefined, { shouldValidate: false });
            clearErrors("codes");
        }

        onSubmit(formData, isCustomCode ? undefined : autoCodeCount);
    };

    return (
        <form
            style={{ maxWidth: "600px", margin: "0 auto", padding: "0 0 2em 0" }}
            onSubmit={(e) => {
                e.preventDefault();
                if (id) {
                    // Edit mode - no codes field
                    const formData = watch();
                    delete formData.codes;
                    delete formData.isCustomCode;
                    onSubmit(formData);
                } else {
                    // Create mode - handle codes properly
                    if (isCustomCode) {
                        handleSubmit(handleFormSubmit)(e);
                    } else {
                        // Auto mode - bypass validation
                        const formData = watch();
                        handleFormSubmit(formData);
                    }
                }
            }}
        >
            <Grid container spacing={1}>
                {/* Custom Codes Switch - Only in Create Mode */}
                {!id && (
                    <Grid item md={12} xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isCustomCode}
                                    onChange={(e) => {
                                        setIsCustomCode(e.target.checked);
                                        if (e.target.checked) {
                                            setCodes([""]);
                                            setValue("codes", [""], { shouldValidate: true });
                                        } else {
                                            setCodes([]);
                                            setValue("codes", undefined, { shouldValidate: false });
                                            clearErrors("codes");
                                        }
                                    }}
                                />
                            }
                            label="Custom Codes"
                        />
                    </Grid>
                )}

                {/* Codes Section - Only in Create Mode */}
                {!id && (
                    <Grid item md={12} xs={12}>
                        <Box sx={{ mb: 2 }}>
                            {isCustomCode ? (
                                <>
                                    {codes.map((code, index) => (
                                        <Box key={index} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}>
                                            <FormText
                                                className={`code-${index} field`}
                                                errorMessage={errors.codes?.[index] as FieldError}
                                                field={{
                                                    value: code,
                                                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleCodeChange(index, e.target.value),
                                                    onBlur: () => {},
                                                    name: `code-${index}`,
                                                    ref: () => {},
                                                }}
                                                label={`Code ${index + 1}`}
                                                placeholder="Enter coupon code"
                                                props={{ required: true, fullWidth: true }}
                                            />
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={async () => {
                                                    try {
                                                        generateCode(1, {
                                                            onSuccess: (data) => {
                                                                if (data.data && data.data.length > 0) {
                                                                    handleCodeChange(index, data.data[0]);
                                                                }
                                                            },
                                                            onError: (error) => {
                                                                console.error("Error generating code:", error);
                                                            },
                                                        });
                                                    } catch (error) {
                                                        console.error("Error generating code:", error);
                                                    }
                                                }}
                                                disabled={isGenerating}
                                                startIcon={
                                                    isGenerating ? (
                                                        <CircularProgress size={16} color="inherit" />
                                                    ) : (
                                                        <Refresh />
                                                    )
                                                }
                                                sx={{
                                                    minWidth: "110px",
                                                    height: "48px",
                                                    fontSize: "0.8rem",
                                                    fontWeight: "600",
                                                    textTransform: "none",
                                                    borderColor: "primary.main",
                                                    color: "primary.main",
                                                    borderRadius: "12px",
                                                    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.15)",
                                                    transition: "all 0.2s ease-in-out",
                                                    marginTop: "21px",
                                                    "&:hover": {
                                                        backgroundColor: "primary.main",
                                                        color: "white",
                                                        borderColor: "primary.main",
                                                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)",
                                                        transform: "translateY(-1px)",
                                                    },
                                                    "&:active": {
                                                        transform: "translateY(0px)",
                                                    },
                                                    "&:disabled": {
                                                        opacity: 0.7,
                                                        cursor: "not-allowed",
                                                        transform: "none",
                                                    },
                                                }}
                                            >
                                                {isGenerating ? "Generating..." : "Generate"}
                                            </Button>
                                            {codes.length > 1 && (
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleRemoveCode(index)}
                                                    sx={{
                                                        minWidth: "auto",
                                                        height: "56px",
                                                        width: "56px",
                                                        borderRadius: "12px",
                                                        boxShadow: "0 2px 8px rgba(211, 47, 47, 0.15)",
                                                        transition: "all 0.2s ease-in-out",
                                                        "&:hover": {
                                                            backgroundColor: "error.main",
                                                            color: "white",
                                                            boxShadow: "0 4px 12px rgba(211, 47, 47, 0.25)",
                                                            transform: "translateY(-1px)",
                                                        },
                                                        "&:active": {
                                                            transform: "translateY(0px)",
                                                        },
                                                    }}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                </>
                            ) : (
                                <FormText
                                    className="autoCodeCount field"
                                    field={{
                                        value: autoCodeCount,
                                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                            const value = parseInt(e.target.value) || 1;
                                            setAutoCodeCount(Math.max(1, value));
                                        },
                                        onBlur: () => {},
                                        name: "autoCodeCount",
                                        ref: () => {},
                                    }}
                                    label="Number of Codes to Generate"
                                    placeholder="Enter number of codes"
                                    props={{ type: "number" }}
                                />
                            )}
                        </Box>
                    </Grid>
                )}

                {/* Title */}
                <Grid item md={12} xs={12}>
                    <Controller
                        key="title"
                        control={control}
                        name="title"
                        render={({ field }) => (
                            <FormText
                                className="title field"
                                errorMessage={errors.title as FieldError}
                                field={{ ...field }}
                                label="Title"
                                placeholder="Enter coupon title"
                                props={{ required: true }}
                            />
                        )}
                    />
                </Grid>

                {/* Description */}
                <Grid item md={12} xs={12}>
                    <Controller
                        key="description"
                        control={control}
                        name="description"
                        render={({ field }) => (
                            <FormTextarea
                                className="description field"
                                errorMessage={errors.description as FieldError}
                                field={{ ...field }}
                                label="Description"
                                placeholder="Enter coupon description"
                                props={{}}
                            />
                        )}
                    />
                </Grid>

                {/* Discount Type */}
                <Grid item md={12} xs={12}>
                    <Controller
                        key="amountType"
                        control={control}
                        name="amountType"
                        render={({ field }) => (
                            <FormSelect
                                className="amountType field"
                                data={amountTypeOptions}
                                errorMessage={errors.amountType as FieldError}
                                field={{ ...field }}
                                label="Discount Type"
                                placeholder="Select discount type"
                                props={{ required: true }}
                            />
                        )}
                    />
                </Grid>

                {/* Amount Value */}
                <Grid item md={12} xs={12}>
                    <Controller
                        key="amountValue"
                        control={control}
                        name="amountValue"
                        render={({ field }) => (
                            <FormText
                                className="amountValue field"
                                errorMessage={errors.amountValue as FieldError}
                                field={{ ...field }}
                                label={amountType === "PERCENTAGE" ? "Discount (%)" : "Amount (USD)"}
                                placeholder={amountType === "PERCENTAGE" ? "Enter discount percentage" : "Enter amount"}
                                props={{ required: true, type: "text" }}
                            />
                        )}
                    />
                </Grid>

                {/* Applied To */}
                <Grid item md={12} xs={12}>
                    <Controller
                        key="appliedTo"
                        control={control}
                        name="appliedTo"
                        render={({ field }) => (
                            <FormSelect
                                className="appliedTo field"
                                data={appliedToOptions}
                                errorMessage={errors.appliedTo as FieldError}
                                field={{ ...field }}
                                label="Applied To"
                                placeholder="Select applied to"
                                props={{ required: true }}
                            />
                        )}
                    />
                </Grid>

                {/* Plans */}
                <Grid item md={12} xs={12}>
                    <Controller
                        key="plans"
                        control={control}
                        name="plans"
                        render={({ field }) => (
                            <FormMultiSelect
                                className="plans field"
                                data={planOptions}
                                errorMessage={errors.plans as FieldError}
                                field={{ ...field }}
                                label="Select Plans (Optional)"
                                placeholder="Select plans"
                                props={{}}
                            />
                        )}
                    />
                </Grid>

                {/* Start Date with Switch */}
                <Grid item md={12} xs={12}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showStartDate}
                                    onChange={(e) => {
                                        setShowStartDate(e.target.checked);
                                        setValue("showStartDate", e.target.checked);
                                        if (e.target.checked) {
                                            setValue("startDate", dayjs().tz(userTimezone).format("YYYY-MM-DDTHH:mm"));
                                        } else {
                                            setValue("startDate", undefined);
                                        }
                                    }}
                                />
                            }
                            label="Set Start Date"
                            sx={{
                                width: "100%",
                                justifyContent: "space-between",
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    flex: 1,
                                },
                            }}
                        />
                        {showStartDate && (
                            <Controller
                                key="startDate"
                                control={control}
                                name="startDate"
                                render={({ field }) => (
                                    <FormText
                                        className="startDate field"
                                        errorMessage={errors.startDate as FieldError}
                                        field={{ ...field }}
                                        label="Start From"
                                        placeholder="Select start date"
                                        props={{ type: "datetime-local" }}
                                    />
                                )}
                            />
                        )}
                    </Box>
                </Grid>

                {/* Expire Date with Switch */}
                <Grid item md={12} xs={12}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={hasExpireDate}
                                    onChange={(e) => {
                                        setHasExpireDate(e.target.checked);
                                        if (e.target.checked) {
                                            setValue(
                                                "expireDate",
                                                dayjs().tz(userTimezone).add(1, "day").format("YYYY-MM-DDTHH:mm")
                                            );
                                        } else {
                                            setValue("expireDate", undefined);
                                        }
                                    }}
                                />
                            }
                            label="Set Expire Date"
                            sx={{
                                width: "100%",
                                justifyContent: "space-between",
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    flex: 1,
                                },
                            }}
                        />
                        {hasExpireDate && (
                            <Controller
                                key="expireDate"
                                control={control}
                                name="expireDate"
                                render={({ field }) => (
                                    <FormText
                                        className="expireDate field"
                                        errorMessage={errors.expireDate as FieldError}
                                        field={{ ...field }}
                                        label="Expire Date"
                                        placeholder="Select expire date"
                                        props={{ type: "datetime-local" }}
                                    />
                                )}
                            />
                        )}
                    </Box>
                </Grid>

                {/* Usage Per User with Switch */}
                <Grid item md={12} xs={12}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showUsagePerUser}
                                    onChange={(e) => {
                                        setShowUsagePerUser(e.target.checked);
                                        setValue("showUsagePerUser", e.target.checked);
                                        if (!e.target.checked) {
                                            setValue("usagePerUser", undefined);
                                        }
                                    }}
                                />
                            }
                            label="Set Usage Per User"
                            sx={{
                                width: "100%",
                                justifyContent: "space-between",
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    flex: 1,
                                },
                            }}
                        />
                        {showUsagePerUser && (
                            <Controller
                                key="usagePerUser"
                                control={control}
                                name="usagePerUser"
                                render={({ field }) => (
                                    <FormText
                                        className="usagePerUser field"
                                        errorMessage={errors.usagePerUser as FieldError}
                                        field={{ ...field }}
                                        label="Usage Per User"
                                        placeholder="Enter usage per user"
                                        props={{ type: "text" }}
                                    />
                                )}
                            />
                        )}
                    </Box>
                </Grid>

                {/* Usage Limit with Switch */}
                <Grid item md={12} xs={12}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showUsageLimit}
                                    onChange={(e) => {
                                        setShowUsageLimit(e.target.checked);
                                        setValue("showUsageLimit", e.target.checked);
                                        if (!e.target.checked) {
                                            setValue("usageLimit", undefined);
                                        }
                                    }}
                                />
                            }
                            label="Set Usage Limit"
                            sx={{
                                width: "100%",
                                justifyContent: "space-between",
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    flex: 1,
                                },
                            }}
                        />
                        {showUsageLimit && (
                            <Controller
                                key="usageLimit"
                                control={control}
                                name="usageLimit"
                                render={({ field }) => (
                                    <FormText
                                        className="usageLimit field"
                                        errorMessage={errors.usageLimit as FieldError}
                                        field={{ ...field }}
                                        label="Usage Limit"
                                        placeholder="Enter usage limit"
                                        props={{ type: "text" }}
                                    />
                                )}
                            />
                        )}
                    </Box>
                </Grid>

                {/* Minimum Price with Switch */}
                <Grid item md={12} xs={12}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showMinimumPrice}
                                    onChange={(e) => {
                                        setShowMinimumPrice(e.target.checked);
                                        setValue("showMinimumPrice", e.target.checked);
                                        if (!e.target.checked) {
                                            setValue("minimumPrice", undefined);
                                        }
                                    }}
                                />
                            }
                            label="Set Minimum Price"
                            sx={{
                                width: "100%",
                                justifyContent: "space-between",
                                margin: 0,
                                "& .MuiFormControlLabel-label": {
                                    flex: 1,
                                },
                            }}
                        />
                        {showMinimumPrice && (
                            <Controller
                                key="minimumPrice"
                                control={control}
                                name="minimumPrice"
                                render={({ field }) => (
                                    <FormText
                                        className="minimumPrice field"
                                        errorMessage={errors.minimumPrice as FieldError}
                                        field={{ ...field }}
                                        label="Minimum Price"
                                        placeholder="Enter minimum price"
                                        props={{ type: "text" }}
                                    />
                                )}
                            />
                        )}
                    </Box>
                </Grid>

                {/* Active Switch - Only in Create Mode */}
                {!id && (
                    <Grid item md={12} xs={12}>
                        <Controller
                            key="active"
                            control={control}
                            name="active"
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                                    }
                                    label="Active"
                                />
                            )}
                        />
                    </Grid>
                )}

                {/* Submit Buttons */}
                <Grid item md={12} xs={12}>
                    <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                        <Button variant="outlined" onClick={() => navigate(-1)}>
                            Cancel
                        </Button>
                        <LoadingButton loading={loading} type="submit" variant="contained">
                            {id ? "Update" : "Create"} Coupon
                        </LoadingButton>
                    </Box>
                </Grid>
            </Grid>
        </form>
    );
}
